import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardSummary } from '@/api/types';
import type { KpiMetrics } from '@marketing-hub/shared';
import { SummaryMetricCard } from '@/features/analytics/SummaryMetricCard';
import { Button } from '@/components/ui/Button';
import { DASHBOARD_METRIC_HINTS } from './dashboardMetricHints';
import { formatPercent } from './kpiRollup';
import { DashboardKpiGridSkeleton } from './DashboardSectionSkeleton';
import { ErrorState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '../../pages/DashboardPage.module.css';

type Props = {
  rangeDays: 7 | 30 | 90;
  summary: DashboardSummary | undefined;
  summaryLoading: boolean;
  summaryError: Error | null;
  rollup: KpiMetrics;
  kpiLoading: boolean;
};

function MetricLink({
  to,
  title,
  children,
}: {
  to: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={styles.metricLinkWrap} title={title}>
      {children}
    </Link>
  );
}

export function DashboardHeroSection({
  rangeDays,
  summary,
  summaryLoading,
  summaryError,
  rollup,
  kpiLoading,
}: Props) {
  if (summaryError) return <ErrorState message={summaryError.message} />;

  const empty = summary != null && summary.totalLeads === 0;
  const showKpiSkeleton = summaryLoading && !summary;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>Overzicht</h2>
          <p className={styles.panelHint}>
            KPI&apos;s voor mail volgen {rangeDays}d. <strong>Nieuw</strong> = laatste 30
            kalenderdagen.
          </p>
        </div>
      </div>

      {empty ? (
        <StateMessage title="Nog geen leads in dit project">
          <p className={styles.muted}>
            Importeer een lijst of wacht op inbound. Mail en Google hieronder werken onafhankelijk.
          </p>
          <div className={styles.emptyActions}>
            <Link to="/app/leads">
              <Button type="button" variant="secondary">
                CSV import
              </Button>
            </Link>
            <Link to="/app/leads">
              <Button type="button" variant="ghost">
                Lead toevoegen
              </Button>
            </Link>
            <Link to="/app/campagnes">
              <Button type="button" variant="ghost">
                Campagnes
              </Button>
            </Link>
          </div>
        </StateMessage>
      ) : null}

      {showKpiSkeleton ? (
        <DashboardKpiGridSkeleton />
      ) : (
        <div className={styles.summaryGrid}>
          <MetricLink to="/app/leads">
            <SummaryMetricCard
              label="Totaal leads"
              value={summary != null ? String(summary.totalLeads) : '—'}
              hideGoalPrompt
              hint={DASHBOARD_METRIC_HINTS.leadsTotal}
            />
          </MetricLink>
          <MetricLink to="/app/leads?status=new">
            <SummaryMetricCard
              label="Nieuw 30d"
              value={summary != null ? String(summary.newLast30Days) : '—'}
              hideGoalPrompt
              hint={DASHBOARD_METRIC_HINTS.leadsNew30d}
            />
          </MetricLink>
          <MetricLink to="/app/campagnes">
            <SummaryMetricCard
              label="Mails verzonden"
              value={kpiLoading ? '…' : String(rollup.mails_sent)}
              hideGoalPrompt
              hint={DASHBOARD_METRIC_HINTS.mailsSent}
            />
          </MetricLink>
          <MetricLink to="/app/campagnes">
            <SummaryMetricCard
              label="Reply rate"
              value={kpiLoading ? '…' : formatPercent(rollup.reply_rate)}
              hideGoalPrompt
              hint={DASHBOARD_METRIC_HINTS.replyRate}
            />
          </MetricLink>
          <MetricLink to="/app/mailboxen">
            <SummaryMetricCard
              label="Bounce rate"
              value={kpiLoading ? '…' : formatPercent(rollup.bounce_rate)}
              hideGoalPrompt
              hint={DASHBOARD_METRIC_HINTS.bounceRate}
              goalTarget={0.03}
              goalDirection="max"
              actualForGoal={kpiLoading ? null : rollup.bounce_rate}
              goalLink="/app/analytics#doelen"
              formatTarget={formatPercent}
            />
          </MetricLink>
        </div>
      )}
    </div>
  );
}
